import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { SaveBar } from "../../utils/SaveBar";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { fetchHseSettings, updateHseSettings } from "../../../api/hse/settings";
import { HseFieldInfoLabel } from "./HseFieldInfoLabel";
import {
  HSE_RISK_HANDLING_KEYS,
  normalizeHseRiskSettings,
  snapshotHseRiskSettings,
  toHseRiskPayload,
} from "./hseCompanySettingsUtils";
import { HseRiskCategoriesEditor } from "./HseRiskCategoriesEditor";

export function HseRiskSettingsForm({ onSaved }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(normalizeHseRiskSettings({}));
  const [savedSnapshot, setSavedSnapshot] = useState(snapshotHseRiskSettings({}));

  const applyLoaded = useCallback((data) => {
    const next = normalizeHseRiskSettings(data);
    setSettings(next);
    setSavedSnapshot(snapshotHseRiskSettings(next));
    onSaved?.(data);
  }, [onSaved]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchHseSettings();
        if (!cancelled) applyLoaded(data);
      } catch {
        if (!cancelled) toast.error(t("hse.settings.load_failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyLoaded, t]);

  const hasChanges = useMemo(
    () => snapshotHseRiskSettings(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patch = (next) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...next }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateHseSettings(toHseRiskPayload(settings));
      applyLoaded(data);
      setSaveSuccess(true);
      toast.success(t("hse.settings.saved"));
      return true;
    } catch {
      setSaveError(true);
      toast.error(t("hse.settings.save_failed"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading />;

  return (
    <>
      <div className="mx-auto max-w-3xl space-y-4 pb-28">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <HseFieldInfoLabel
              label="hse.settings.risk.handling_title"
              infoTitle="hse.settings.risk.handling_info_title"
              infoBody="hse.settings.risk.handling_info_body"
            />
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.risk.handling_description")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {HSE_RISK_HANDLING_KEYS.map((key) => {
              const active = settings.handling === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => patch({ handling: key })}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                    active
                      ? "bg-[#1463C7] text-white shadow-[0_8px_16px_-8px_rgba(20,99,199,.6)]"
                      : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {t(`hse.settings.risk.handling.${key}`)}
                </button>
              );
            })}
          </div>
          {settings.handling === "custom" ? (
            <textarea
              value={settings.handling_notes}
              onChange={(event) => patch({ handling_notes: event.target.value })}
              rows={4}
              className="mt-4 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              placeholder={t("hse.settings.risk.handling_notes_placeholder")}
            />
          ) : null}
        </section>

        <HseRiskCategoriesEditor />

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
            <input
              type="checkbox"
              checked={settings.skip_requires_reason === true}
              onChange={(event) => patch({ skip_requires_reason: event.target.checked })}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">
                {t("hse.settings.risk.skip_requires_reason")}
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {t("hse.settings.risk.skip_requires_reason_help")}
              </p>
            </div>
          </label>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              {t("hse.settings.risk.responsible_title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.risk.responsible_description")}
            </p>
          </div>
          <EmployeeSelector
            name="risk_responsible_user_id"
            label="hse.settings.risk.responsible_title"
            values={{ risk_responsible_user_id: settings.responsible_user_id }}
            setFieldValue={(_, value) => patch({ responsible_user_id: value || null })}
            selected={settings.responsible_user_id ? { id: settings.responsible_user_id } : null}
          />
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h3 className="text-base font-semibold text-slate-900">
            {t("hse.settings.risk.assessors_title")}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {t("hse.settings.risk.assessors_description")}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {settings.assessor_user_ids.length === 0 ? (
              <p className="text-sm text-slate-500">{t("hse.settings.risk.assessors_empty")}</p>
            ) : (
              settings.assessor_user_ids.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-800"
                >
                  #{id}
                  <button
                    type="button"
                    onClick={() =>
                      patch({
                        assessor_user_ids: settings.assessor_user_ids.filter((item) => item !== id),
                      })
                    }
                    className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                    aria-label={t("hse.settings.risk.remove_assessor")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="mt-4">
            <EmployeeSelector
              name="assessor_user_id"
              label="hse.settings.risk.add_assessor"
              values={{ assessor_user_id: null }}
              excludeUserIds={settings.assessor_user_ids}
              setFieldValue={(_, value) => {
                if (!value || settings.assessor_user_ids.includes(value)) return;
                patch({ assessor_user_ids: [...settings.assessor_user_ids, value] });
              }}
              selected={null}
            />
          </div>
        </section>
      </div>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("hse.settings.saved")}
        errorMessage={t("hse.settings.save_failed")}
        onSave={handleSave}
        saveLabel={t("save")}
      />
    </>
  );
}
